import { Settings, Mail, Image as ImageIcon, HardDrive, Key, Gauge, ScrollText, Bell } from "lucide-react";
import { Link } from "wouter";
import { PageHeading, Section, Callout, RoleBadge, pathFor, Diagram, MockTabs } from "../shared";

export default function SettingsTabsSection() {
  return (
    <div data-testid="guide-settings-tabs">
      <PageHeading
        icon={Settings}
        title="Settings Tabs — Overview"
        description="The Settings page is the 'engine room' of the system. It holds the technical bits that don't change very often — sending email, the company logo, backups, and a few performance and diagnostic tools."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="it_admin" />
        <span className="text-xs text-white/40">Most tabs are owned by the Tech (IT) Admin. The tabs you can see depend on the permissions your role has been given.</span>
      </div>

      <Callout variant="info" title="In plain words">
        Think of Settings as the back-office controls a computer person looks after. You won't visit it day-to-day. It's kept separate from <span className="text-white">Admin</span> (which is for running the work) so nobody accidentally changes something like the email password while doing their normal job.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li>You need to set up or fix the emails the system sends.</li>
          <li>You want to change the company name or logo.</li>
          <li>You need to take a backup, or restore one.</li>
          <li>You're connecting an outside AI/assistant tool, or checking why something broke.</li>
        </ul>
      </Section>

      <Section title="Finding the tabs">
        <p className="text-sm text-white/65 leading-relaxed">
          Open <span className="text-white">Settings</span> from the gear icon in the header. Along the top you'll see a row of tabs — each one is a different control panel. Click a tab to switch to it.
        </p>
        <Diagram
          frameLabel="Settings"
          legend={[
            <><span className="text-white">Email Settings (SMTP)</span> — how the system sends email, plus who gets IT alerts.</>,
            <><span className="text-white">Branding</span> — company name, department name, logo, and the website address used in emails.</>,
            <><span className="text-white">Backup &amp; Restore</span> — save a copy of everything, or put a saved copy back.</>,
            <><span className="text-white">Agent API Keys</span> — passes for outside AI tools to connect.</>,
            <><span className="text-white">Live Refresh &amp; Performance</span> — how often the screen pulls in fresh data.</>,
            <><span className="text-white">System Diagnostics Log</span> — a record of errors and events for IT to read.</>,
          ]}
        >
          <MockTabs
            tabs={[
              { label: "Email (SMTP)", active: true, marker: 1 },
              { label: "Branding", marker: 2 },
              { label: "Backup & Restore", marker: 3 },
              { label: "Agent API Keys", marker: 4 },
              { label: "Live Refresh", marker: 5 },
              { label: "System Log", marker: 6 },
            ]}
          />
        </Diagram>
      </Section>

      <Section title="What each tab does">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SettingsCard slug="settings/email" icon={Mail} name="Email Settings (SMTP)" role="it_admin">
            <p>The mailbox details the system uses to send every notification. Has a Test button so you can check it works. You re-enter your password to save the credentials.</p>
          </SettingsCard>
          <SettingsCard slug="settings/alert-dispatcher" icon={Bell} name="IT Alert Dispatcher" role="it_admin">
            <p>Who gets emailed when something serious breaks, plus a once-a-day summary. Lives inside the Email tab because it shares the same mailbox login.</p>
          </SettingsCard>
          <SettingsCard slug="settings/branding" icon={ImageIcon} name="Branding" role="it_admin">
            <p>Company name, department name, logo, and the website address. These show up on the sign-in page, the header, and inside emails.</p>
          </SettingsCard>
          <SettingsCard slug="settings/backup" icon={HardDrive} name="Backup & Restore" role="it_admin">
            <p>Download a single file holding everything in the database, restore from an earlier one, or download an install package for an offline computer.</p>
          </SettingsCard>
          <SettingsCard slug="settings/api-keys" icon={Key} name="Agent API Keys (Jeeves)" role="it_admin">
            <p>Create and cancel the passes that let outside AI helpers connect. You re-enter your password to create or cancel one.</p>
          </SettingsCard>
          <SettingsCard slug="settings/live-refresh" icon={Gauge} name="Live Refresh & Performance" role="it_admin">
            <p>Choose how often screens fetch fresh data. Pick a ready-made preset or hand-tune it. The factory default is the gentle "Conservative" setting.</p>
          </SettingsCard>
          <SettingsCard slug="settings/system-log" icon={ScrollText} name="System Diagnostics Log" role="it_admin">
            <p>One place that records errors and important events for IT — email problems, sign-in issues, database hiccups and more. Can be exported and clears itself after 90 days.</p>
          </SettingsCard>
        </div>
      </Section>

      <Callout variant="warning" title="Why Settings is separate from Admin">
        Admin is for the day-to-day running of work. Settings is for the technical plumbing that rarely changes. Keeping them apart means nobody accidentally edits something like the email password while doing routine tasks.
      </Callout>

      <Callout variant="info" title="Looking for the full developer reference?">
        If you're connecting an outside tool, see <Link href={pathFor("agent-api")} className="text-cyan-300 hover:underline">Agent API (Jeeves)</Link> for the technical details.
      </Callout>
    </div>
  );
}

function SettingsCard({ slug, icon: Icon, name, role, children }: { slug: string; icon: any; name: string; role: string; children: any }) {
  return (
    <div className="p-3 rounded-md bg-white/[0.02] border border-white/5" data-testid={`settings-tab-${name.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4 text-cyan-300" />
        <p className="text-sm text-white font-medium">{name}</p>
        <RoleBadge role={role} />
      </div>
      <div className="text-xs text-white/55 leading-relaxed space-y-1 mb-2">{children}</div>
      <Link href={pathFor(slug)} className="text-xs text-cyan-300 hover:underline" data-testid={`link-readmore-${slug.replace(/\//g, "-")}`}>Read more →</Link>
    </div>
  );
}
